import { useCallback, useEffect, useState } from "react";

const ORIENTATION_QUERY = "(orientation: landscape)";

const getRequestFullscreen = (el) =>
  el?.requestFullscreen ||
  el?.webkitRequestFullscreen ||
  el?.mozRequestFullScreen ||
  el?.msRequestFullscreen;

const getExitFullscreen = () =>
  document.exitFullscreen ||
  document.webkitExitFullscreen ||
  document.mozCancelFullScreen ||
  document.msExitFullscreen;

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement;

const useOrientationLayout = () => {
  const [isLandscape, setIsLandscape] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(ORIENTATION_QUERY)?.matches
  );
  const [isFullscreenActive, setIsFullscreenActive] = useState(
    () => typeof document !== "undefined" && !!getFullscreenElement()
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(ORIENTATION_QUERY);
    const handleChange = (e) => setIsLandscape(e.matches);
    mql.addEventListener?.("change", handleChange);
    return () => mql.removeEventListener?.("change", handleChange);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreenActive(!!getFullscreenElement());
    const events = ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"];
    events.forEach((evt) => document.addEventListener(evt, handleFullscreenChange));
    return () => events.forEach((evt) => document.removeEventListener(evt, handleFullscreenChange));
  }, []);

  const enterFullscreenLandscape = useCallback((elementRef) => {
    const el = elementRef?.current;
    const requestFullscreen = getRequestFullscreen(el);

    if (!requestFullscreen) {
      return Promise.resolve({ supported: false });
    }

    return requestFullscreen
      .call(el)
      .then(() => {
        if (window.screen?.orientation?.lock) {
          return window.screen.orientation.lock("landscape").catch(() => {});
        }
        return undefined;
      })
      .then(() => ({ supported: true }))
      .catch(() => ({ supported: false }));
  }, []);

  const exitFullscreenLandscape = useCallback(() => {
    window.screen?.orientation?.unlock?.();
    const exitFullscreen = getExitFullscreen();
    if (exitFullscreen && getFullscreenElement()) {
      exitFullscreen.call(document).catch(() => {});
    }
  }, []);

  return { isLandscape, isFullscreenActive, enterFullscreenLandscape, exitFullscreenLandscape };
};

export default useOrientationLayout;
